import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  CheckCircle as ApproveIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { VENUES, type Venue } from "@restaurant-staff-portal/shared";
import type { CashOutWithCalculations } from "@/types/cashOut";
import { getUserFullName, isCashOutApproved } from "@/types/cashOut";

interface CashOutListProps {
  cashOuts: CashOutWithCalculations[];
  totalCount: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onApprove?: (id: string) => void;
  onExport: () => void;
  isLoading?: boolean;
  venue?: Venue;
}

export function CashOutList({
  cashOuts,
  totalCount,
  page,
  limit,
  onPageChange,
  onLimitChange,
  onView,
  onEdit,
  onApprove,
  onExport,
  isLoading,
  venue,
}: CashOutListProps) {
  const { user } = useAuth();
  const { cashOutPermissions } = usePermissions();

  const getStatusColor = (cashOut: CashOutWithCalculations) => {
    if (isCashOutApproved(cashOut)) return "success";
    if (cashOut.approver) return "error";
    return "warning";
  };

  const getStatusLabel = (cashOut: CashOutWithCalculations) => {
    if (isCashOutApproved(cashOut)) return "Approved";
    if (cashOut.approver) return "Rejected";
    return "Pending";
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ width: "100%", mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Cash Outs {venue && `- ${venue}`}</Typography>
        {cashOutPermissions.canExportCSV && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onExport}
          >
            Export CSV
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Venue</TableCell>
              <TableCell>Server</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cashOuts.map((cashOut) => (
              <TableRow key={cashOut.id}>
                <TableCell>
                  {format(new Date(cashOut.entryDate), "MMM dd, yyyy")}
                </TableCell>
                <TableCell>
                  {VENUES[cashOut.venue as keyof typeof VENUES] ||
                    cashOut.venue}
                </TableCell>
                <TableCell>{getUserFullName(cashOut.user)}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(cashOut)}
                    color={getStatusColor(cashOut)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                  >
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => onView(cashOut.id)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {(cashOutPermissions.canEditAll ||
                      cashOut.user?.id === user?.id) &&
                      !isCashOutApproved(cashOut) && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(cashOut.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                    {cashOutPermissions.canApprove &&
                      !isCashOutApproved(cashOut) &&
                      onApprove && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            onClick={() => onApprove(cashOut.id)}
                            color="success"
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {cashOuts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No cash outs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
      />
    </Paper>
  );
}
